import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Car, Users, CurrencyInr, PencilSimple, Star, Envelope, MapTrifold, Images } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabaseClient';
import { normalizeVehicle } from '@/hooks/useVehicles';
import AdminNav from '@/components/admin/AdminNav';
import VehiclesTab from '@/components/admin/VehiclesTab';
import DriversTab from '@/components/admin/DriversTab';
import TestimonialsTab from '@/components/admin/TestimonialsTab';
import RoutesTab from '@/components/admin/RoutesTab';
import GalleryTab from '@/components/admin/GalleryTab';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
};

function parseLanguages(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function enrichDrivers(drivers, vehicles) {
  const byId = Object.fromEntries(vehicles.map((v) => [v.id, v]));
  return drivers.map((d) => {
    const vehicle = d.assigned_vehicle_id ? byId[d.assigned_vehicle_id] : null;
    return {
      ...d,
      languages: parseLanguages(d.languages),
      assigned_vehicle_label: vehicle
        ? `${vehicle.model}${vehicle.registration_number ? ` (${vehicle.registration_number})` : ''}`
        : null,
    };
  });
}

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('cab-rates');
  const [inquiries, setInquiries] = useState([]);
  const [leads, setLeads] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [cabFares, setCabFares] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [routePages, setRoutePages] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [editingFare, setEditingFare] = useState(null);
  const [fareForm, setFareForm] = useState({ name: '', seats: '', rate_per_km: '', min_fare: '' });
  const [loading, setLoading] = useState(false);
  const editDriverRef = useRef(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        inquiriesRes,
        leadsRes,
        vehiclesRes,
        driversRes,
        faresRes,
        testimonialsRes,
        routesRes,
        galleryRes,
      ] = await Promise.all([
        supabase.from('inquiries').select('*').order('created_at', { ascending: false }),
        supabase.from('leads').select('*').order('created_at', { ascending: false }),
        supabase
          .from('vehicles')
          .select('*, vehicle_images(*)')
          .order('type')
          .order('model'),
        supabase.from('drivers').select('*').order('name'),
        supabase.from('cab_fares').select('*').order('sort_order').order('name'),
        supabase
          .from('testimonials')
          .select('*')
          .order('display_order')
          .order('created_at', { ascending: false }),
        supabase.from('route_pages').select('*').order('sort_order').order('title'),
        supabase
          .from('gallery_photos')
          .select('*')
          .order('sort_order')
          .order('created_at', { ascending: false }),
      ]);

      const firstError =
        inquiriesRes.error ||
        leadsRes.error ||
        vehiclesRes.error ||
        driversRes.error ||
        faresRes.error ||
        testimonialsRes.error ||
        routesRes.error ||
        galleryRes.error;
      if (firstError) throw firstError;

      const vehicleList = (vehiclesRes.data || []).map(normalizeVehicle);
      setInquiries(inquiriesRes.data || []);
      setLeads(leadsRes.data || []);
      setVehicles(vehicleList);
      setDrivers(enrichDrivers(driversRes.data || [], vehicleList));
      setCabFares(faresRes.data || []);
      setTestimonials(testimonialsRes.data || []);
      setRoutePages(routesRes.data || []);
      setGalleryPhotos(galleryRes.data || []);
    } catch (error) {
      toast.error(`Failed to load data${error.message ? ` — ${error.message}` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  const activeDrivers = useMemo(
    () => drivers.filter((d) => d.status === 'available').length,
    [drivers],
  );

  const expiringLicenses = useMemo(
    () =>
      drivers
        .filter((d) => {
          const days = daysUntil(d.license_expiry);
          return days !== null && days >= 0 && days < 30;
        })
        .sort((a, b) => new Date(a.license_expiry) - new Date(b.license_expiry)),
    [drivers],
  );

  const openFareEdit = (fare) => {
    setEditingFare(fare);
    setFareForm({
      name: fare.name,
      seats: fare.seats,
      rate_per_km: String(fare.rate_per_km),
      min_fare: String(fare.min_fare),
    });
  };

  const saveFare = async () => {
    if (!editingFare) return;
    try {
      const { error } = await supabase
        .from('cab_fares')
        .update({
          name: fareForm.name.trim(),
          seats: fareForm.seats.trim(),
          rate_per_km: parseFloat(fareForm.rate_per_km),
          min_fare: parseFloat(fareForm.min_fare),
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingFare.id);
      if (error) throw error;
      toast.success('Cab rate updated — changes appear across the site');
      setEditingFare(null);
      fetchAllData();
    } catch {
      toast.error('Failed to update cab rate');
    }
  };

  const toggleFareActive = async (fare) => {
    try {
      const { error } = await supabase
        .from('cab_fares')
        .update({
          is_active: !fare.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', fare.id);
      if (error) throw error;
      toast.success(fare.is_active ? 'Rate hidden from site' : 'Rate visible on site');
      fetchAllData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleLicenseUpdate = (driver) => {
    setActiveTab('drivers');
    setTimeout(() => editDriverRef.current?.(driver), 100);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <AdminNav />

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-atithi-navy mb-8 font-display" data-testid="admin-heading">
          Dashboard
        </h1>

        {expiringLicenses.length > 0 && (
          <div className="mb-5 space-y-2">
            {expiringLicenses.map((driver) => (
              <div
                key={driver.id}
                className="flex items-center justify-between gap-4 bg-[#FFF3E0] border-l-4 border-[#E8651A] rounded-lg px-5 py-3.5"
              >
                <p className="text-sm font-body text-[#1B2D5E]">
                  <span className="mr-2">⚠️</span>
                  License expiring soon: <strong>{driver.name}</strong> — expires {driver.license_expiry}
                </p>
                <button
                  type="button"
                  onClick={() => handleLicenseUpdate(driver)}
                  className="text-sm font-semibold text-[#E8651A] hover:underline shrink-0 font-body"
                >
                  Update →
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="grid md:grid-cols-5 gap-6 mb-8">
          <Card className="border-atithi-border cursor-pointer hover:border-[#E8651A]/40 transition-colors" onClick={() => setActiveTab('inquiries')}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-atithi-muted">WhatsApp Inquiries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-atithi-navy">{leads.length}</div>
              {leads.filter((l) => l.status === 'New').length > 0 && (
                <p className="text-xs text-[#E8651A] mt-1 font-medium">
                  {leads.filter((l) => l.status === 'New').length} new
                </p>
              )}
            </CardContent>
          </Card>
          <Card className="border-atithi-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-atithi-muted">Package Inquiries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-atithi-navy">{inquiries.length}</div>
            </CardContent>
          </Card>
          <Card className="border-atithi-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-atithi-muted">Vehicles</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-atithi-navy">{vehicles.length}</div>
            </CardContent>
          </Card>
          <Card className="border-atithi-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-atithi-muted">Drivers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-atithi-navy">{drivers.length}</div>
            </CardContent>
          </Card>
          <Card className="border-atithi-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-atithi-muted">Active Drivers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-atithi-navy">{activeDrivers}</div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 mb-8 h-auto gap-1">
            <TabsTrigger value="inquiries" data-testid="inquiries-tab" className="text-xs sm:text-sm py-2.5">
              <Envelope size={18} className="mr-1 sm:mr-2 shrink-0" />
              Inquiries
              {leads.filter((l) => l.status === 'New').length > 0 && (
                <span className="ml-1 sm:ml-2 bg-[#E8651A] text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                  {leads.filter((l) => l.status === 'New').length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="cab-rates" data-testid="cab-rates-tab">
              <CurrencyInr size={18} className="mr-2" />
              Cab Rates
            </TabsTrigger>
            <TabsTrigger value="vehicles" data-testid="vehicles-tab">
              <Car size={18} className="mr-2" />
              Vehicles
            </TabsTrigger>
            <TabsTrigger value="drivers" data-testid="drivers-tab">
              <Users size={18} className="mr-2" />
              Drivers
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="testimonials-tab">
              <Star size={18} className="mr-2" />
              Testimonials
            </TabsTrigger>
            <TabsTrigger value="routes" data-testid="routes-tab">
              <MapTrifold size={18} className="mr-2" />
              Routes
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="gallery-tab">
              <Images size={18} className="mr-2" />
              Gallery
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries">
            <Card className="border-atithi-border">
              <CardHeader className="flex flex-row items-center justify-between gap-4">
                <div>
                  <CardTitle className="font-display">Customer Inquiries</CardTitle>
                  <p className="text-sm text-[#888] font-body mt-1">
                    WhatsApp booking requests from the homepage and booking pages
                  </p>
                </div>
                <Link
                  to="/admin/leads"
                  className="text-sm font-semibold text-[#E8651A] hover:underline shrink-0 font-body"
                >
                  Open full view →
                </Link>
              </CardHeader>
              <CardContent>
                {leads.length === 0 ? (
                  <p className="text-center text-sm text-[#888] py-10 font-body">
                    No inquiries yet. Bookings from the site will appear here.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Route</TableHead>
                          <TableHead>Travel date</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.slice(0, 10).map((lead) => (
                          <TableRow key={lead.id}>
                            <TableCell className="font-medium">{lead.name || '—'}</TableCell>
                            <TableCell>{lead.phone}</TableCell>
                            <TableCell>
                              {lead.from_city} → {lead.to_city}
                            </TableCell>
                            <TableCell>{lead.travel_date || '—'}</TableCell>
                            <TableCell>
                              <Badge className="bg-[#FFF3E0] text-[#E8651A]">{lead.status || 'New'}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-atithi-border mt-8">
              <CardHeader>
                <CardTitle className="font-display">Package Inquiries</CardTitle>
                <p className="text-sm text-[#888] font-body mt-1">
                  Tour package booking requests from the Packages page
                </p>
              </CardHeader>
              <CardContent>
                {inquiries.length === 0 ? (
                  <p className="text-center text-sm text-[#888] py-10 font-body">
                    No package inquiries yet.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Package</TableHead>
                          <TableHead>Travel date</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {inquiries.slice(0, 10).map((inq) => (
                          <TableRow key={inq.id}>
                            <TableCell className="font-medium">{inq.name || '—'}</TableCell>
                            <TableCell>{inq.phone}</TableCell>
                            <TableCell>{inq.email || '—'}</TableCell>
                            <TableCell>{inq.package_name || '—'}</TableCell>
                            <TableCell>{inq.travel_date || '—'}</TableCell>
                            <TableCell>
                              <Badge className="bg-[#E3F2FD] text-[#1565C0]">{inq.status || 'pending'}</Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cab-rates">
            <Card className="border-atithi-border">
              <CardHeader>
                <CardTitle className="font-display">Cab Fare Rates</CardTitle>
                <p className="text-sm text-[#888] font-body">
                  Edit per-km rates for route cards and fare calculator
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Seats</TableHead>
                        <TableHead>Rate/km</TableHead>
                        <TableHead>Min fare</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cabFares.map((fare) => (
                        <TableRow key={fare.id} data-testid={`cab-fare-row-${fare.slug}`}>
                          <TableCell className="font-medium">{fare.name}</TableCell>
                          <TableCell>{fare.seats}</TableCell>
                          <TableCell className="font-semibold text-[#E8651A]">₹{fare.rate_per_km}/km</TableCell>
                          <TableCell>₹{fare.min_fare}</TableCell>
                          <TableCell>
                            <Badge className={fare.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                              {fare.is_active ? 'Active' : 'Hidden'}
                            </Badge>
                          </TableCell>
                          <TableCell className="space-x-2">
                            <Button size="sm" variant="outline" onClick={() => openFareEdit(fare)}>
                              <PencilSimple size={16} className="mr-1" />
                              Edit
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleFareActive(fare)}>
                              {fare.is_active ? 'Hide' : 'Show'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="vehicles">
            <VehiclesTab vehicles={vehicles} onRefresh={() => fetchAllData()} />
          </TabsContent>

          <TabsContent value="drivers">
            <DriversTab
              drivers={drivers}
              vehicles={vehicles}
              onRefresh={() => fetchAllData()}
              onEditDriver={editDriverRef}
            />
          </TabsContent>

          <TabsContent value="testimonials">
            <TestimonialsTab testimonials={testimonials} onRefresh={() => fetchAllData()} />
          </TabsContent>

          <TabsContent value="routes">
            <RoutesTab routes={routePages} onRefresh={() => fetchAllData()} />
          </TabsContent>

          <TabsContent value="gallery">
            <GalleryTab photos={galleryPhotos} onRefresh={() => fetchAllData()} />
          </TabsContent>
        </Tabs>

        {loading && (
          <p className="text-center text-sm text-[#888] font-body mt-4">Refreshing data…</p>
        )}
      </div>

      <Dialog open={!!editingFare} onOpenChange={(open) => !open && setEditingFare(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Edit Cab Rate</DialogTitle>
            <DialogDescription>
              {editingFare?.slug} — updates route cards and fare estimates
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Display name</Label>
              <Input value={fareForm.name} onChange={(e) => setFareForm((p) => ({ ...p, name: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Seats</Label>
              <Input value={fareForm.seats} onChange={(e) => setFareForm((p) => ({ ...p, seats: e.target.value }))} className="mt-1" placeholder="e.g. 4+1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rate per km (₹)</Label>
                <Input type="number" min="1" step="0.5" value={fareForm.rate_per_km} onChange={(e) => setFareForm((p) => ({ ...p, rate_per_km: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label>Minimum fare (₹)</Label>
                <Input type="number" min="0" step="50" value={fareForm.min_fare} onChange={(e) => setFareForm((p) => ({ ...p, min_fare: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <Button onClick={saveFare} className="w-full bg-[#1B2D5E] hover:bg-[#E8651A]">Save rate</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
